import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { FaSearch } from "react-icons/fa";
import { useContext } from "react";
import { Link, useLocation ,useNavigate} from "react-router-dom";
import AuthProvider from "../context/AuthContext";
import { apiGet } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";
import obj from "../Utils/helpers";
const SearchWrap = () => {
  let { setSearchToogle, searchToogle } = useContext(AuthProvider);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const allEventsRef = useRef(null);
  const keywordRef = useRef(keyword);
  const desktopInputRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const [desktopTarget, setDesktopTarget] = useState(null);

  useEffect(() => {
    keywordRef.current = keyword;
  }, [keyword]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useLayoutEffect(() => {
    setDesktopTarget(isDesktop ? document.getElementById("desktop-header-search") : null);
  }, [isDesktop, location.pathname]);

  const closeDesktopSearch = () => {
    setSearchToogle(false);
    setKeyword("");
    setList([]);
    document.getElementById("desktop-search-toggle")?.focus();
  };

  useEffect(() => {
    if (!desktopTarget || !searchToogle) return;
    desktopInputRef.current?.focus();
    const onEscape = event => {
      if (event.key === "Escape" && desktopTarget.contains(event.target)) closeDesktopSearch();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [desktopTarget, searchToogle]);

  // Load all matches once when search opens
  const ensureEventsLoaded = async () => {
    if (allEventsRef.current) return allEventsRef.current;
    setLoading(true);
    try {
      const { status, data } = await apiGet(apiPath.matchList + "?game_type=all");
      if (status === 200 && data?.status) {
        const matches = data?.data?.matches || [];
        const events = matches.map(m => ({
          eventId: String(m.eventId || m.id),
          marketId: m.market?.marketId || m.marketId || "",
          eventName: m.name || m.eventName || "",
          eventDateTime: m.openDateTime ? new Date(m.openDateTime).toISOString() : null,
        }));
        allEventsRef.current = events;
        return events;
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
    return [];
  };

  const applyFilter = (key) => {
    const events = allEventsRef.current || [];
    const term = (key || "").trim().toLowerCase();
    if (!term) { setList([]); return; }
    const filtered = events
      .filter(e => e.eventName && e.eventName.toLowerCase().includes(term))
      .slice(0, 50);
    setList(filtered);
    setLoading(false);
  };

  useEffect(() => {
    if (searchToogle) {
      ensureEventsLoaded().then(() => applyFilter(keywordRef.current));
    }
  }, [searchToogle]);

  useEffect(() => {
    setSearchToogle(false);
    setKeyword("");
    setList([]);
    allEventsRef.current = null;
  }, [location]);

  if (desktopTarget) {
    return searchToogle ? createPortal(
      <div className="desktop-inline-search">
        <form className="desktop-search-field" role="search" onSubmit={event => { event.preventDefault(); applyFilter(keyword); }}>
          <FaSearch aria-hidden="true" />
          <input
            ref={desktopInputRef}
            type="text"
            aria-label="Search events"
            autoCapitalize="off"
            autoComplete="off"
            value={keyword}
            onChange={event => { setKeyword(event.target.value); applyFilter(event.target.value); }}
          />
          <button type="button" aria-label="Close search" onClick={closeDesktopSearch}>
            <svg viewBox="0 0 20 20" width="20" height="20" fill="none" aria-hidden="true">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </form>
        {keyword.trim() && (
          <div className="desktop-search-results" aria-label="Match search results" aria-busy={loading}>
            {loading ? <p role="status">Searching…</p> : list.length ? (
              <ul>
                {list.map(item => (
                  <li key={`${item.eventId}:${item.marketId}`}>
                    <Link to={`/match-details/${item.eventId}/${item.marketId}`} onClick={closeDesktopSearch}>
                      {item.eventName}
                      {item.eventDateTime && <small>{obj.matchDateTime(item.eventDateTime)}</small>}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : <p role="status">No events found matching "{keyword}".</p>}
          </div>
        )}
      </div>,
      desktopTarget
    ) : null;
  }

  return (
    <div
      id="searchWrap"
      className="overlay style09"
      style={{
        display: searchToogle ? "block" : "none",
        zIndex: 9999,
        background: "#00000080",
      }}
    >
      <div id="searchPop" className="search-pop">
        <div style={{ position: "relative" }}>
          <a
            id="searchBack"
            className="search-back ui-link"
            onClick={() => {
              setSearchToogle(false);
              setKeyword("");
              setList([]);
            }}
            href="javascript:void(0)"
          >
            Back
          </a>
          <input
            id="searchInput"
            className="search-input"
            data-role="none"
            type="text"
            autoCapitalize="off"
            autoComplete="off"
            value={keyword}
            onChange={(e) => {
              const v = e.target.value;
              setKeyword(v);
              applyFilter(v);
            }}
            autoFocus
            placeholder="Search Events"
          />
          <button
            id="searchClear"
            className="search-clear ui-btn ui-shadow ui-corner-all"
            type="reset"
            onClick={() => {
              setKeyword("");
              setList([]);
            }}
          >
            Reset
          </button>
          <button
            id="searchBtn"
            className="search-collapse ui-btn ui-shadow ui-corner-all"
            onClick={() => applyFilter(keyword)}
          >
            Search
          </button>
        </div>
        <div
          id="searchResult"
          className="suggestion-wrap"
          style={{ display: "block" }}
        >
          {loading && keyword && (
            <div style={{ padding: "12px 16px", color: "#666" }}>
              Searching…
            </div>
          )}
          {!loading && keyword && list.length === 0 && (
            <div style={{ padding: "12px 16px", color: "#666" }}>
              No events found matching "{keyword}".
            </div>
          )}
          {list?.length > 0 && (
            <ul id="searchList" style={{ display: "block" }}>
              {list.length > 0 &&
                list.map((item) => {
                  return (
                    <li
                      id="resultTemplate"
                      // onClick={() => {
                      //   setSearchToogle(false);
                      //   setKeyword("");
                      //   setList([]);
                      // }}
                      style={{ display: "block" }}
                    >
                      <Link
                        to={`/match-details/${item.eventId}/${item.marketId}`}
                        className="ui-link"
                      >
                        {obj.matchDateTime(item.eventDateTime)}
                        <span
                          id="marketTime"
                          style={{ paddingLeft: "5px" }}
                          className="time"
                        >
                          {item.eventName}
                        </span>
                      </Link>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchWrap;
